import React, { useState } from 'react';
import MovieDetailSheet from './MovieDetailSheet';

const TMDB_IMG = 'https://image.tmdb.org/t/p/w342';

// Generate a Cinemark /movies/ slug from a title.
// Cinemark slug rules (observed from live site):
//   1. Lowercase
//   2. & → "and"  (MUST come before the strip step so "and" survives)
//   3. Strip everything that isn't a letter, digit, or space
//      — apostrophes, colons, commas, hyphens, exclamation marks, periods all dropped
//   4. Trim, then collapse spaces → hyphens
// Examples:
//   "You, Me & Tuscany"         → "you-me-and-tuscany"
//   "Spider-Man: Brand New Day" → "spider-man-brand-new-day"
//   "Don't Move"                → "dont-move"
//   "M3GAN 2.0"                 → "m3gan-20"
function toCinemarkSlug(title) {
  return title
    .toLowerCase()
    .replace(/&/g, 'and')          // & → "and"  (must precede the strip step)
    .replace(/[^a-z0-9\s]/g, '')   // drop apostrophes, colons, commas, hyphens, etc.
    .trim()
    .replace(/\s+/g, '-');
}

function formatDate(d) {
  if (!d) return 'TBA';
  return new Date(d + 'T00:00:00').toLocaleDateString('en-US', {
    month: 'short', day: 'numeric', year: 'numeric',
  });
}


function ShimmerCard() {
  return <div className="shimmer-card" />;
}
export { ShimmerCard };

export default function MovieCard({ movie, inWatchlist, onWatchlistToggle, isLoggedIn, isNowShowing }) {
  const [imgLoaded,  setImgLoaded]  = useState(false);
  const [imgError,   setImgError]   = useState(false);
  const [busy,       setBusy]       = useState(false);
  const [hovering,   setHovering]   = useState(false);
  const [sheetOpen,  setSheetOpen]  = useState(false);

  function handleCardClick() {
    setSheetOpen(true);
  }

  const poster = movie.poster_path && !imgError
    ? `${TMDB_IMG}${movie.poster_path}`
    : null;

  // Shared placeholder shown when no poster exists or image fails to load
  const Placeholder = (
    <div className="poster-placeholder">
      <span className="poster-placeholder-emoji">🎬</span>
    </div>
  );

  async function handleAlert(e) {
    e.stopPropagation();
    if (busy || !onWatchlistToggle) return;
    setBusy(true);
    try { await onWatchlistToggle(movie.id, inWatchlist); }
    finally { setBusy(false); }
  }

  // Badge — only show when it adds information
  let badge = null;
  if (movie.special_screening) {
    badge = { cls: 'badge-special', label: 'Special Screening' };
  } else if (isNowShowing) {
    badge = { cls: 'badge-theaters', label: 'Now Showing' };
  } else if (movie.tickets_on_sale) {
    badge = { cls: 'badge-on-sale', label: 'Tickets On Sale' };
  }

  return (
    <>
    <div
      className={`movie-card${!poster ? ' movie-card--placeholder' : ''}`}
      onClick={handleCardClick}
    >
      {/* Poster */}
      <div className="card-poster">
        {poster ? (
          <>
            {!imgLoaded && <div className="shimmer-card" style={{ position: 'absolute', inset: 0, borderRadius: 0 }} />}
            <img
              src={poster}
              alt={movie.title}
              onLoad={() => setImgLoaded(true)}
              onError={() => setImgError(true)}
              style={{ opacity: imgLoaded ? 1 : 0 }}
            />
          </>
        ) : (
          Placeholder
        )}
      </div>

      {/* Gradient overlay */}
      <div className="card-overlay" />

      {/* Badge — only rendered when it adds information */}
      {badge && <span className={`card-badge ${badge.cls}`}>{badge.label}</span>}

      {/* Bottom content */}
      <div className="card-body">
        <div className="card-date">{formatDate(movie.release_date)}</div>
<div className="card-title">{movie.title}</div>

        <div className="card-actions" onClick={e => e.stopPropagation()}>
          {(isNowShowing || movie.tickets_on_sale) ? (
            /* NOW SHOWING or TICKETS ON SALE: show theater buttons only when URLs are valid */
            <>
              {movie.megaplex_url && (
                <a
                  href={movie.megaplex_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn-ticket-mega"
                >
                  🎟 Megaplex
                </a>
              )}
              <a
                href={movie.cinemark_url || `https://www.cinemark.com/movies/${toCinemarkSlug(movie.title)}`}
                target="_blank"
                rel="noopener noreferrer"
                className="btn-ticket-cine"
              >
                🎟 Cinemark
              </a>
            </>
          ) : isLoggedIn ? (
            /* COMING SOON + logged in: alert toggle */
            <button
              className={`btn-alert${inWatchlist ? ' alerted' : ''}`}
              onClick={handleAlert}
              disabled={busy}
              onMouseEnter={() => setHovering(true)}
              onMouseLeave={() => setHovering(false)}
            >
              {busy ? '…' : inWatchlist
                ? (hovering ? '✕ Remove Alert' : '✓ Alert Active')
                : '🔔 Alert Me'}
            </button>
          ) : (
            /* COMING SOON + not logged in: prompt sign up */
            <button
              className="btn-alert"
              onClick={handleAlert}
            >
              🔔 Alert Me
            </button>
          )}
        </div>
      </div>
    </div>

    {/* Mobile detail sheet — rendered into document.body via portal */}
    {sheetOpen && (
      <MovieDetailSheet
        movie={movie}
        inWatchlist={inWatchlist}
        onWatchlistToggle={onWatchlistToggle}
        isLoggedIn={isLoggedIn}
        isNowShowing={isNowShowing}
        onClose={() => setSheetOpen(false)}
      />
    )}
    </>
  );
}
